import type { ShiftCode } from '../engine/types'
import { ShiftCodeChip } from './ShiftCodeChip'

export interface RosterShiftRowProps {
  /** Already formatted for display, e.g. 'Tue 26 May'. */
  date: string
  code: ShiftCode
  /** Already formatted, e.g. '06:30–16:30'. */
  timeRange: string
  /**
   * The shift carries an OPEN advertisement (Phase 4). Renders the
   * "Advertised" badge beside the chip — a state the calendar must show
   * honestly, because an advertised shift is public and its owner needs to
   * know that at a glance.
   */
  advertised?: boolean
}

/**
 * One roster shift as a row, designed to sit inside Station Ledger's
 * `ShiftList`. Carries the date, the time range and the shift-code chip, plus
 * the Phase 4 advertised badge when the shift is on the feed. In `ShiftRow`'s
 * spirit without its money: no amount, no breakdown, no actions.
 *
 * Read-only by construction — this component takes props and renders. No
 * `onClick`, no interactive element. Offered/swapped treatments are Phase 5;
 * neither is anticipated here.
 *
 * `date` and `timeRange` arrive pre-formatted, the same contract
 * `ShiftRow.tsx` establishes — date maths stays with the caller, this
 * component only lays words out.
 */
export function RosterShiftRow({ date, code, timeRange, advertised = false }: RosterShiftRowProps) {
  return (
    <div className="ss-roster-row">
      <span className="ss-roster-row__when">
        <span className="ss-roster-row__date">{date}</span>
        <span className="ss-roster-row__time">{timeRange}</span>
      </span>
      <span className="ss-roster-row__chip">
        <ShiftCodeChip code={code} />
        {advertised ? (
          <span className="ss-advertised-badge" title="Advertised for swap">
            Advertised
          </span>
        ) : null}
      </span>
    </div>
  )
}
